import type { OidcConfig } from '@tagna/auth';

export interface OidcDiscoveryResult {
  authority: string;
  config: OidcConfig;
  metadata: Record<string, unknown>;
}

export const DEFAULT_AUTHORITY = 'https://localhost:44311';
const DEFAULT_CLIENT_ID = 'Admin_App';
const DEFAULT_SCOPE = 'openid profile email offline_access Identity Identity.Admin Engagement';
const REQUIRED_SCOPES = [
  'openid',
  'profile',
  'email',
  'offline_access',
  'Identity',
  'Identity.Admin',
  'Engagement',
];

const REQUIRED_METADATA_FIELDS = [
  'authorization_endpoint',
  'token_endpoint',
  'issuer',
];

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/+$/, '');
}

function isLocalHostname(hostname: string): boolean {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '0.0.0.0' ||
    hostname.endsWith('.local')
  );
}

function isLocalUrl(url: string): boolean {
  try {
    return isLocalHostname(new URL(url).hostname);
  } catch {
    return /localhost|127\.0\.0\.1|0\.0\.0\.0/i.test(url);
  }
}

function isLocalRuntime(): boolean {
  if (import.meta.env.DEV) {
    return true;
  }

  try {
    return isLocalHostname(window.location.hostname);
  } catch {
    return false;
  }
}

export function resolveRuntimeOidcAuthority(
  authorityEnv?: string,
): string {
  const configured = normalizeBaseUrl(authorityEnv?.trim() || DEFAULT_AUTHORITY);
  const appIsLocal = isLocalRuntime();

  // Local safety guard: never call remote identity from localhost app sessions.
  if (appIsLocal && !isLocalUrl(configured)) {
    console.warn(
      `[OIDC] App is running locally but authority is "${configured}". ` +
        `Using local authority "${DEFAULT_AUTHORITY}" instead.`,
    );
    return DEFAULT_AUTHORITY;
  }

  return configured;
}

export async function loadOidcConfig(): Promise<OidcDiscoveryResult> {
  const authorityEnv = import.meta.env.VITE_OIDC_AUTHORITY as string | undefined;
  const clientIdEnv = import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined;
  const scopeEnv = import.meta.env.VITE_OIDC_SCOPE as string | undefined;

  const authorityBase = resolveRuntimeOidcAuthority(authorityEnv);
  const discoveryUrl = `${authorityBase}/.well-known/openid-configuration`;

  const response = await fetch(discoveryUrl, {
    method: 'GET',
    cache: 'no-store',
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error(`Failed to load OIDC discovery document: ${response.status} ${response.statusText}`);
  }

  const metadata = (await response.json()) as Record<string, unknown>;

  for (const field of REQUIRED_METADATA_FIELDS) {
    if (!metadata[field]) {
      throw new Error(`OIDC discovery document is missing required field: ${field}`);
    }
  }

  const metadataIssuer =
    typeof metadata.issuer === 'string'
      ? normalizeBaseUrl(metadata.issuer)
      : authorityBase;
  const issuer = isLocalRuntime() ? authorityBase : metadataIssuer;

  const redirectUri = new URL('/auth/callback', window.location.origin).toString();
  const postLogoutRedirectUri = new URL('/', window.location.origin).toString();
  const silentRedirectUri = new URL('/silent-renew.html', window.location.origin).toString();

  const scopeTokens = new Set(
    (scopeEnv?.trim() || DEFAULT_SCOPE).split(/\s+/).filter(Boolean),
  );
  REQUIRED_SCOPES.forEach((scope) => scopeTokens.add(scope));
  const scope = Array.from(scopeTokens).join(' ');

  const config: OidcConfig = {
    authority: issuer,
    client_id: clientIdEnv?.trim() || DEFAULT_CLIENT_ID,
    redirect_uri: redirectUri,
    post_logout_redirect_uri: postLogoutRedirectUri,
    scope,
    automaticSilentRenew: true,
    silent_redirect_uri: silentRedirectUri,
  };

  return {
    authority: issuer,
    config,
    metadata,
  };
}
